// Companion/useCyclingPlaceholder.ts
import { useEffect, useState } from 'react';

/**
 * A placeholder whose ending cycles: `lead` stays put and each of `tails`
 * takes a turn after it.
 */
export interface CyclingPlaceholderSpec {
  lead: string;
  tails: readonly string[];
}

/**
 * What the empty chat page's composer suggests, one ending at a time. These
 * replaced the greeting's "Ask about a topic, your performance, or how Ora
 * works." subheading: the same three offers, shown where the student is about
 * to type rather than as a line of copy above it.
 */
export const CHAT_PAGE_PLACEHOLDER: CyclingPlaceholderSpec = {
  lead: 'Ask about ',
  tails: ['your performance…', 'a topic…', 'how Ora works…'],
};

/** How long each ending sits in the box before the next one drops in. */
export const PLACEHOLDER_HOLD_MS = 2800;
/** How long the drop-out takes, matched by the `chat-cycle-out` keyframes. */
export const PLACEHOLDER_FADE_MS = 420;

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export interface CyclingPlaceholder {
  /** The ending the box currently shows. */
  text: string;
  /** Which ending it is; a new value means a new element, which animates in. */
  index: number;
  /** The current ending is dropping out; the next one replaces it when it is gone. */
  isFading: boolean;
}

/**
 * Cycles through `tails`, fading each one out before the next appears.
 *
 * `paused` holds the cycle on the ending it is showing (the box has focus and
 * shows only its cursor), so the text does not jump when the student clicks
 * out of an empty box. Under `prefers-reduced-motion` the first ending stays
 * put: a line of text that keeps changing is the kind of motion that setting
 * asks us not to make.
 */
export const useCyclingPlaceholder = (
  tails: readonly string[],
  paused: boolean
): CyclingPlaceholder => {
  const [index, setIndex] = useState(0);
  const [isFading, setIsFading] = useState(false);

  useEffect(() => {
    if (paused || tails.length < 2 || prefersReducedMotion()) {
      setIsFading(false);
      return;
    }

    let fadeTimer: ReturnType<typeof setTimeout> | undefined;

    const holdTimer = setTimeout(() => {
      setIsFading(true);

      fadeTimer = setTimeout(() => {
        setIndex((current) => (current + 1) % tails.length);
        setIsFading(false);
      }, PLACEHOLDER_FADE_MS);
    }, PLACEHOLDER_HOLD_MS);

    return () => {
      clearTimeout(holdTimer);
      if (fadeTimer !== undefined) clearTimeout(fadeTimer);
    };
  }, [index, paused, tails]);

  return { text: tails[index % tails.length] ?? '', index, isFading };
};
